import { Heading, Paragraph } from '@customerio/pluma-components/react';
import { ImportType } from '@/services/db/sql-import/entity';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import { QueryEditor, type usePreviewRun } from '@/features/sql-sync';
import type { SqlImportDraft } from '../draft';

type QueryStepProps = {
  type: SqlDatabaseType;
  draft: SqlImportDraft;
  /** The name of the object type the import writes to, for the heading. */
  objectTypeName: string | undefined;
  isNewImport: boolean;
  isReadOnly: boolean;
  canIdentifyByEmail: boolean;
  run: ReturnType<typeof usePreviewRun>;
  onQueryChange: (query: string) => void;
  onLocationChange: (location: string) => void;
  onRun: () => void;
  onGoToSettings: () => void;
  onTimeoutDismiss: () => void;
  onTimeoutContinue: () => void;
};

/** The Query step: the import's query, run against the chosen database for a preview. */
export function QueryStep({
  type,
  draft,
  objectTypeName,
  isNewImport,
  isReadOnly,
  canIdentifyByEmail,
  run,
  onQueryChange,
  onLocationChange,
  onRun,
  onGoToSettings,
  onTimeoutDismiss,
  onTimeoutContinue,
}: QueryStepProps) {
  const isObjectImport = draft.import_type === ImportType.object;
  return (
    <QueryEditor
      databaseType={type}
      query={draft.query ?? ''}
      onQueryChange={onQueryChange}
      location={draft.location}
      onLocationChange={onLocationChange}
      header={
        <Heading level="3" color="bold" mb="050">
          {isObjectImport && objectTypeName != null
            ? `Write a query returning the ${objectTypeName} to sync.`
            : 'Write your query'}
        </Heading>
      }
      intro={
        <Paragraph as="span">
          {isNewImport ? "We've provided an example query to help you get started." : null}
        </Paragraph>
      }
      isReadOnly={isReadOnly}
      canIdentifyByEmail={canIdentifyByEmail}
      onGoToSettings={onGoToSettings}
      run={run}
      onRun={onRun}
      onTimeoutDismiss={onTimeoutDismiss}
      onTimeoutContinue={onTimeoutContinue}
    />
  );
}
